import React from "react";
import { money } from "@/lib/format";
import { InfoTip } from "@/components/common";
import { Backpack, ArrowsClockwise, Money, GraduationCap, Wrench, Certificate } from "@phosphor-icons/react";
import { Progress } from "@/components/ui/progress";
import { higherEdRoom } from "@/lib/overWithdrawnSplit";

const DISCLAIMER = "Not tax advice — rules change; confirm with your tax advisor.";
const EDU_INFOTIP = "Over qualified costs, the earnings portion of the excess is subject to income tax plus a 10% federal penalty. The penalty (not the tax) is waived in some cases, e.g., scholarships, disability, or amounts used for education credits. Guidance only, not tax advice — rules change; confirm with your tax advisor.";

function capColor(remaining, cap) {
  if (remaining <= 0) return { text: "text-[#DC2626]", bg: "bg-[#DC2626]" };
  if (cap > 0 && remaining / cap <= 0.15) return { text: "text-[#D97706]", bg: "bg-[#D97706]" };
  return { text: "text-[#22C55E]", bg: "bg-[#22C55E]" };
}

function CapLine({ label, remaining, claimed, cap, testid }) {
  const pct = cap > 0 ? Math.min(100, Math.round((claimed / cap) * 100)) : 0;
  const clr = capColor(remaining, cap);
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-[#374151]">{label}</span>
        <span className={`font-semibold ${clr.text}`}>
          {remaining > 0 ? `${money(remaining, "USD")} left` : "at limit"}
        </span>
      </div>
      <Progress value={pct} className="mt-1 h-1.5" indicatorClassName={`${clr.bg} !bg-none`} />
      <div className="mt-0.5 text-[11px] text-[#6b7280] tnum">{money(claimed, "USD")} of {money(cap, "USD")} used</div>
    </div>
  );
}

/* ── Education-use line (withdrawn/qualified instead of claimed/cap) ── */
function EduLine({ label, remaining, withdrawn, qualified, lines, year, testid }) {
  const pct = qualified > 0 ? Math.min(100, Math.round((withdrawn / qualified) * 100)) : 0;
  const clr = capColor(remaining, qualified);
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-[#374151]">{label}</span>
        <span className={`font-semibold ${clr.text}`} data-testid={testid && `${testid}-room`}>
          {remaining > 0.005 ? `${money(remaining, "USD")} room` : remaining < -0.005 ? `${money(-remaining, "USD")} over` : "at limit"}
        </span>
      </div>
      <Progress value={pct} className="mt-1 h-1.5" indicatorClassName={`${clr.bg} !bg-none`} />
      <div className="mt-0.5 text-[11px] text-[#6b7280] tnum">{money(withdrawn, "USD")} withdrawn of {money(qualified, "USD")} qualified</div>
      {lines && (
        <div className="text-[10px] text-[#6b7280] tnum" data-testid={testid && `${testid}-paid-to-come`}>
          {money(lines.paid, "USD")} already paid
          {lines.awaitingRate > 0.005 && <> + {money(lines.awaitingRate, "USD")} waiting for a final exchange rate</>}
          {" "}+ {money(lines.toCome, "USD")} still to come
          {lines.aid > 0.005 && <> − {money(lines.aid, "USD")} scholarships</>}
          {lines.reserve > 0.005 && <> − {money(lines.reserve, "USD")} AOTC reserve</>}
        </div>
      )}
    </div>
  );
}

/* ── Statutory hard-cap cards ── */

function K12Card({ cap, taxYear, compact }) {
  return (
    <div className="rounded-lg border border-[#C2C9D1] bg-white p-3" data-testid="dist-cap-k12">
      <div className="flex items-center gap-2">
        <Backpack size={18} weight="bold" className="shrink-0 text-[#034AA2]" />
        <span className="text-sm font-bold text-[#0A0A0A]">K-12 Tuition</span>
        <InfoTip testid="dist-cap-k12-info" width="w-72">
          Tuition above the cap isn&apos;t qualified; the earnings portion of the excess is subject to income tax plus a 10% federal penalty. Guidance only, not tax advice — rules change; confirm with your tax advisor.
        </InfoTip>
      </div>
      <div className="mt-2.5">
        <CapLine label={`${taxYear} annual cap`} remaining={cap.remaining} claimed={cap.claimed} cap={cap.cap} testid="dist-cap-k12-line" />
      </div>
      {!compact && (
        <p className="mt-2 text-[11px] leading-relaxed text-[#6b7280]">{money(cap.cap, "USD")} a year for K-12 tuition ({taxYear}).</p>
      )}
      <p className="mt-1 text-[10px] text-[#6b7280]">{money(cap.cap, "USD")} per beneficiary per year ({taxYear}) &mdash; combined across all their 529 accounts.</p>
      <p className="mt-1.5 text-[10px] text-[#9CA3AF]">{DISCLAIMER}</p>
    </div>
  );
}

function RothCard({ lifetime, annual, taxYear, compact }) {
  return (
    <div className="rounded-lg border border-[#C2C9D1] bg-white p-3" data-testid="dist-cap-roth">
      <div className="flex items-center gap-2">
        <ArrowsClockwise size={18} weight="bold" className="shrink-0 text-[#0369A1]" />
        <span className="text-sm font-bold text-[#0A0A0A]">Roth IRA Rollover</span>
        <InfoTip testid="dist-cap-roth-info" width="w-80">
          Over the annual or $35,000 lifetime limit is treated as an excess Roth IRA contribution — a 6% excise tax per year until you correct it. A rollover that doesn&apos;t qualify (not a direct trustee-to-trustee transfer, or the 529 open less than 15 years) is instead a non-qualified distribution: earnings taxed plus a 10% penalty. Guidance only, not tax advice — rules change; confirm with your tax advisor.
        </InfoTip>
      </div>
      <div className="mt-2.5 space-y-2">
        {lifetime && <CapLine label="Lifetime" remaining={lifetime.remaining} claimed={lifetime.claimed} cap={lifetime.cap} testid="dist-cap-roth-lifetime" />}
        {lifetime && annual && <div className="border-t border-[#E9E9EB]" />}
        {annual && <CapLine label={`${taxYear} annual`} remaining={annual.remaining} claimed={annual.claimed} cap={annual.cap} testid="dist-cap-roth-annual" />}
      </div>
      {!compact && (
        <p className="mt-2 text-[11px] leading-relaxed text-[#6b7280]">
          $35,000 lifetime, capped each year at the Roth IRA contribution limit
          ({annual ? `${money(annual.cap, "USD")} in ${taxYear}` : "$7,500"}).
          Requires a direct trustee-to-trustee transfer (the 529 sends the money straight to the Roth IRA — you never receive it); the 529 must have been open at least 15 years.
        </p>
      )}
      <p className="mt-1 text-[10px] text-[#6b7280]">$35,000 lifetime per beneficiary &mdash; combined across accounts.</p>
      <p className="mt-1.5 text-[10px] text-[#9CA3AF]">{DISCLAIMER}</p>
    </div>
  );
}

function LoanCard({ cap, compact }) {
  return (
    <div className="rounded-lg border border-[#C2C9D1] bg-white p-3" data-testid="dist-cap-loan">
      <div className="flex items-center gap-2">
        <Money size={18} weight="bold" className="shrink-0 text-[#0F766E]" />
        <span className="text-sm font-bold text-[#0A0A0A]">Student Loan</span>
        <InfoTip testid="dist-cap-loan-info" width="w-72">
          Amounts above the $10,000 lifetime cap aren&apos;t qualified; the earnings portion of the excess is subject to income tax plus a 10% federal penalty. Each of the beneficiary&apos;s siblings has their own separate $10,000 lifetime limit. Guidance only, not tax advice — rules change; confirm with your tax advisor.
        </InfoTip>
      </div>
      <div className="mt-2.5">
        <CapLine label="Lifetime" remaining={cap.remaining} claimed={cap.claimed} cap={cap.cap} testid="dist-cap-loan-line" />
      </div>
      {!compact && (
        <p className="mt-2 text-[11px] leading-relaxed text-[#6b7280]">$10,000 lifetime toward student loans.</p>
      )}
      <p className="mt-1 text-[10px] text-[#6b7280]">$10,000 lifetime per beneficiary &mdash; combined across accounts; each sibling has a separate $10,000.</p>
      <p className="mt-1.5 text-[10px] text-[#9CA3AF]">{DISCLAIMER}</p>
    </div>
  );
}

/* ── Education-use cards ── */

const EDU_CONFIG = {
  post_secondary: { icon: GraduationCap, color: "text-[#034AA2]", summary: "No fixed dollar cap — bounded by qualified costs; room and board capped at the greater of the school's cost of attendance (COA) or the amount it invoices." },
  apprenticeship: { icon: Wrench, color: "text-[#0F766E]", summary: "No fixed dollar cap — fees, books, supplies, and equipment for a program registered with the Department of Labor (DOL)." },
  credentialing: { icon: Certificate, color: "text-[#0369A1]", summary: "No fixed dollar cap — a recognized postsecondary credential program under the One Big Beautiful Bill Act (OBBBA)." },
};

function EduCard({ grp, taxYear, compact, aqee }) {
  const cfg = EDU_CONFIG[grp.type];
  if (!cfg) return null;
  // Higher Education reads the same figures as the Safe to withdraw card
  // (ZEN-807); the other groups keep their own.
  const shared = grp.type === "post_secondary" ? higherEdRoom(aqee) : null;
  const fig = shared
    ? { remaining: shared.room, withdrawn: shared.withdrawn, qualified: shared.qualified, lines: shared.lines }
    : { remaining: grp.remaining, withdrawn: grp.withdrawn, qualified: grp.qualified, lines: null };
  const Icon = cfg.icon;
  return (
    <div className="rounded-lg border border-[#C2C9D1] bg-white p-3" data-testid={`dist-edu-${grp.type}`}>
      <div className="flex items-center gap-2">
        <Icon size={18} weight="bold" className={`shrink-0 ${cfg.color}`} />
        <span className="text-sm font-bold text-[#0A0A0A]">{grp.label}</span>
        <InfoTip testid={`dist-edu-${grp.type}-info`} width="w-80">{EDU_INFOTIP}</InfoTip>
      </div>
      <div className="mt-2.5">
        <EduLine
          label={`${taxYear} qualified costs`}
          remaining={fig.remaining} withdrawn={fig.withdrawn} qualified={fig.qualified} lines={fig.lines}
          testid={`dist-edu-${grp.type}-line`}
        />
      </div>
      {!compact && (
        <p className="mt-2 text-[11px] leading-relaxed text-[#6b7280]">{cfg.summary}</p>
      )}
      {grp.type === "post_secondary" && (
        <p className="mt-1 text-[10px] text-[#6b7280]">Room &amp; board: for an at-least-half-time beneficiary, off-campus is capped at the school&rsquo;s published room-and-board allowance; on-campus housing the school owns or operates can use the actual amount charged when it&rsquo;s higher.</p>
      )}
      <p className="mt-1.5 text-[10px] text-[#9CA3AF]">{DISCLAIMER}</p>
    </div>
  );
}

/* ── Main panel ── */

export function DistributionSummary({ statutoryCaps, groupCaps, aqee, beneficiaryName, taxYear, compact = false }) {
  const byType = Object.fromEntries((statutoryCaps || []).map((c) => [c.type, c]));
  const k12 = byType.k12_tuition;
  const rothLife = byType.roth_ira;
  const rothAnnual = byType.roth_annual;
  const loan = byType.student_loan;

  const showK12 = k12 && k12.claimed > 0;
  const showRoth = (rothLife && rothLife.claimed > 0) || (rothAnnual && rothAnnual.claimed > 0);
  const showLoan = loan && loan.claimed > 0;
  const hasHardCaps = showK12 || showRoth || showLoan;

  const eduCards = (groupCaps || []).filter((g) => g.qualified > 0);
  const hasEduCards = eduCards.length > 0;

  if (!hasHardCaps && !hasEduCards) return null;

  return (
    <div className="mb-4 rounded-xl border border-[#C2C9D1] bg-[#F8FAFC] p-4" data-testid="distribution-summary">
      {/* Header */}
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-[#034AA2]">Distribution summary</h3>
        {beneficiaryName && (
          <span className="text-xs font-semibold text-[#6b7280]">{beneficiaryName} · {taxYear}</span>
        )}
      </div>
      {!compact && (
        <p className="mt-0.5 text-[11px] text-[#6b7280]">All figures below are for this beneficiary.</p>
      )}

      {/* Education use cards */}
      {hasEduCards && (
        <>
          <div className="mt-3 mb-2 text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Education use · bounded by qualified costs</div>
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
            {eduCards.map((g) => <EduCard key={g.type} grp={g} taxYear={taxYear} compact={compact} aqee={aqee} />)}
          </div>
        </>
      )}

      {/* Statutory hard caps */}
      {hasHardCaps && (
        <>
          <div className={`${hasEduCards ? "mt-4" : "mt-3"} mb-2 text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]`}>Statutory dollar caps · hard limits</div>
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
            {showK12 && <K12Card cap={k12} taxYear={taxYear} compact={compact} />}
            {showRoth && <RothCard lifetime={rothLife} annual={rothAnnual} taxYear={taxYear} compact={compact} />}
            {showLoan && <LoanCard cap={loan} compact={compact} />}
          </div>
        </>
      )}
    </div>
  );
}
